import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, ArrowRight } from 'lucide-react';

import { openCustomerPortal, PORTAL_BASE_URL } from '../../../utils/cn';

// "Answers — plainly" FAQ section (2026-05-23). Sits between
// ServiceWarrantySection and SanityContentSection (the blog) on the
// homepage per the brief — "above blog section."
//
// LAYOUT:
//   • Desktop (lg+): 2-col grid. Left column = sticky heading + intro
//     (stays in view while user scrolls through the cards). Right column
//     = stacked FAQ cards.
//   • Mobile/tablet: single column. Heading on top, cards below.
//
// CARD BEHAVIOR:
//   • Each card is a collapsible disclosure. Cyan-filled circle icon at
//     the top-right toggles X (expanded) ↔ + (collapsed).
//   • DEFAULT STATE: all cards open (matches the mockup screenshots
//     exactly — user can collapse what they don't need).
//   • Height/opacity animated via AnimatePresence so collapse/expand
//     reads smoothly.
//
// BOTTOM CTA BAR:
//   • Separate light gray-blue strip below the FAQ. Centered white pill
//     contains the prompt + cyan button → opens the portal in a new tab
//     via openCustomerPortal (same handler as other homepage CTAs).
//
// ACCESSIBILITY:
//   • Each toggle is a real <button> with aria-expanded set from state.
//   • Answer panels use aria-hidden when collapsed.
//   • Focus-visible cyan ring on toggle buttons.

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// Q&A content — verbatim from the brief screenshots. The "Can I cancel"
// answer has bolded `72 hours of signing`, so its `answer` is a JSX
// fragment rather than a plain string.
const faqs = [
  {
    question: "If I pay $0 down, what's stopping you from disappearing?",
    answer:
      "Backward question, and a fair one — but it works the other way. You're the one holding the leverage at $0 down. We've already paid for your materials, scheduled the crew, and pulled permits — all before any money moves. If anything goes wrong, you haven't paid yet. The risk is on our side, not yours. We do this because we're confident in the install. The Google reviews and our willingness to put it in writing tell you the rest.",
  },
  {
    question: 'What happens after I enter my address?',
    answer:
      "You'll get a line-itemed quote texted and emailed to you in about 30 minutes. After that, one OpenbooQ specialist may follow up by phone — not a salesperson, not a pitch. Just a real roofer making sure the quote makes sense and answering anything you're unsure about. Take the call or skip it; either way, we won't blow up your phone, and your info never leaves OpenbooQ.",
  },
  {
    question: 'Are these the same materials a local roofer would install?',
    answer:
      'Yes — and you actually get more brand choice than at most local roofers. OpenbooQ installs premium shingles from GAF (Timberline HDZ), Owens Corning (Oakridge, Duration), Atlas (Pinnacle Pristine), and TAMKO (Titan XT) — the top residential manufacturers in the U.S. — sourced from the same regional distributors local roofers use. The shingle on your roof is identical to what a traditional roofer would install. The difference is what we charge for it: wholesale cost, no markup.',
  },
  {
    question: 'Who actually installs the roof?',
    answer:
      "OpenbooQ-managed crews led by manufacturer-certified install leads we've personally vetted. Body cameras worn for the entire job. Your assigned project manager is on-site or on-call from tear-off through cleanup. We're not subcontracting to a random crew off Craigslist — we're a roofing company that hires and supervises its own people.",
  },
  {
    question: "What if I'm not happy with the install?",
    answer:
      "You don't approve, you don't pay. Simple as that. We don't process payment until you walk the finished roof and sign off. After approval, the 5-year workmanship warranty covers any issue we caused. The 30-year+ manufacturer warranty covers the materials. And the body-cam footage from your install is on file — we can review any concern with the actual recording, not your-word-against-ours.",
  },
  {
    question: 'How can you possibly charge this much less than my local roofer?',
    answer:
      "Two reasons. First, no commissioned salespeople. Traditional roofers pay 10-25% of every job in commission — that's $1,500-$5,000 on a $20,000 roof, baked into the price you pay. We don't have salespeople. Second, no material markup. Most roofers add 50-100% to material cost as profit. We pass materials through at wholesale. Add it up and that's where the 30-50% savings comes from. We make our money on our flat 20% Service & Warranty fee, on volume, on doing this efficiently — not on hiding margin in your invoice.",
  },
  {
    question: 'Can I cancel after signing?',
    answer: (
      <>
        Yes — free of charge within <strong className="font-bold" style={{ color: '#000150' }}>72 hours of signing</strong>. After that window, a 25% cancellation fee applies. Here's why: once you sign, we immediately begin scheduling your install, ordering your materials, and locking in a crew for your project date. After 72 hours, real costs and commitments are in motion on your behalf — the fee covers what's already been put against your job. Either way, no surprise charges: the 72-hour window and the 25% fee are spelled out clearly in your contract before you sign.
      </>
    ),
  },
];

// ─── FAQ card ───────────────────────────────────────────────────────────

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.5, delay: index * 0.05, ease: EASE_OUT_EXPO }}
    className="rounded-2xl overflow-hidden bg-white"
    style={{
      border: '1.5px solid rgba(7, 176, 233, 0.55)',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    }}
  >
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-start justify-between gap-4 p-5 lg:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#07B0E9] focus-visible:ring-offset-2 rounded-2xl"
    >
      <h3
        className="font-bold text-base lg:text-lg leading-snug flex-1"
        style={{ color: '#000150' }}
      >
        {question}
      </h3>
      {/* Cyan filled circle toggle — X when open, + when closed. */}
      <span
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
        style={{
          background: '#07B0E9',
          boxShadow: '0 2px 4px rgba(7, 176, 233, 0.35)',
        }}
        aria-hidden="true"
      >
        {isOpen ? (
          <X size={14} strokeWidth={2.8} color="white" />
        ) : (
          <Plus size={14} strokeWidth={2.8} color="white" />
        )}
      </span>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
          style={{ overflow: 'hidden' }}
        >
          <div className="px-5 lg:px-6 pb-5 lg:pb-6">
            <p className="text-sm lg:text-[15px] leading-relaxed text-gray-600">
              {answer}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.article>
);

// ─── Section ────────────────────────────────────────────────────────────

const FAQSection = () => {
  // Initial state — all CLOSED (2026-05-24). Users click the cyan
  // toggle (or the question text) to expand each answer. The mockup
  // showed all expanded for content review, but the live UX defaults
  // to collapsed so the section is scannable and doesn't dominate the
  // page on first load. `Set<number>` of open indices for O(1) toggle.
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (i) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleCtaClick = (e) => {
    e.preventDefault();
    openCustomerPortal();
  };

  return (
    <section className="bg-white">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-12 lg:gap-16">
          {/* LEFT — heading + intro. Sticky on lg+ so it stays visible
              while scrolling through the long stack of cards on the right.
              Each element fades up on scroll-into-view with a brief
              cascade (eyebrow → headline → subhead). The headline's
              "plainly." span gets a subtle continuous cyan-glow pulse
              once the entrance settles — pulls eye to the cyan accent
              without being distracting. */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, ease: EASE_OUT_EXPO }}
              className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
              style={{ color: '#07B0E9' }}
            >
              — Questions homeowners ask us
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-[1.05] tracking-tight"
              style={{
                color: '#000150',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.02em',
              }}
            >
              Answers —{' '}
              <motion.span
                style={{ color: '#07B0E9', display: 'inline-block' }}
                animate={{
                  filter: [
                    'drop-shadow(0 0 0 rgba(7, 176, 233, 0))',
                    'drop-shadow(0 0 18px rgba(7, 176, 233, 0.55))',
                    'drop-shadow(0 0 0 rgba(7, 176, 233, 0))',
                  ],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  times: [0, 0.5, 1],
                  delay: 1.2,
                }}
              >
                plainly.
              </motion.span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO }}
              className="text-gray-500 text-base lg:text-lg leading-relaxed max-w-[420px]"
            >
              If something's missing, text us. We'll add it. No bots, no scripts.
            </motion.p>
          </div>

          {/* RIGHT — stacked FAQ cards. */}
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={openSet.has(i)}
                onToggle={() => toggle(i)}
                index={i}
              />
            ))}
          </div>
        </div>
      </div>

      {/* BOTTOM CTA BAR — light gray-blue strip with a centered white
          pill containing the prompt + the cyan portal button. */}
      <div style={{ background: '#F0F4F8' }}>
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 lg:py-10">
          <div
            className="bg-white rounded-full px-5 sm:px-6 py-3 sm:py-3.5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 max-w-2xl mx-auto"
            style={{ boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)' }}
          >
            <p
              className="text-sm lg:text-base text-center sm:text-left font-medium"
              style={{ color: '#000150' }}
            >
              Questions answered? Let's get your number.
            </p>
            <a
              href={PORTAL_BASE_URL}
              onClick={handleCtaClick}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full font-bold text-sm whitespace-nowrap transition-all duration-200 flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #07B0E9 0%, #0596C7 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(7, 176, 233, 0.4)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 18px rgba(7, 176, 233, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(7, 176, 233, 0.4)';
              }}
            >
              <span>Get my roof price</span>
              <ArrowRight size={14} strokeWidth={2.4} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
